/**
 * DetailHeader — the booking detail page's header: back + Bookings breadcrumb
 * + customer name. On desktop the code + status sit inline on the right;
 * below `lg` they move to a status band (BON-145).
 *
 * Split out of `BookingDetailPage.tsx` — `useNavigate` makes this a real
 * component (`react/no-multi-comp` allows only one per file), not a plain
 * helper function like the hook-free `statusPill` it renders.
 */
import { ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import type { BookingDetail } from '../../api/bookingsV2';
import { useFocusHeadingOnNavigate } from '../../hooks/useFocusHeadingOnNavigate';

import { statusDisplay } from './bookingDetailModel';

function statusPill(booking: BookingDetail) {
  const status = statusDisplay(booking.status);
  return (
    <div className="flex items-center gap-2">
      {booking.isComp && (
        <span className="text-xs px-2.5 py-1 rounded-md bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-medium">
          Comped
        </span>
      )}
      <span
        className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md ${status.badge}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
        {status.label}
      </span>
    </div>
  );
}

export function DetailHeader({
  booking,
  isDesktop,
}: {
  booking: BookingDetail;
  isDesktop: boolean;
}) {
  const navigate = useNavigate();
  const headingRef = useFocusHeadingOnNavigate<HTMLHeadingElement>();
  const customerName = [booking.customer.firstName, booking.customer.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const codeText = booking.displayRef;

  return (
    <>
      <div
        className={`flex items-start gap-3 px-6 sm:px-8 lg:px-12 pt-8 pb-4 ${
          isDesktop ? 'border-b border-stone-200 dark:border-stone-700' : ''
        }`}
      >
        <button
          onClick={() => navigate('/admin/bookings')}
          aria-label="Back to bookings"
          className="mt-1 flex-shrink-0 p-1.5 rounded-lg text-stone-400 hover:bg-stone-100 dark:hover:bg-[#2a3530]"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="min-w-0 flex-1">
          <Link
            to="/admin/bookings"
            className="text-xs font-semibold uppercase tracking-wide text-stone-400 hover:text-moss-600 dark:hover:text-moss-400"
          >
            Bookings
          </Link>
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="mt-0.5 truncate text-2xl font-bold text-bark-900 dark:text-stone-100 focus:outline-none"
          >
            {customerName || booking.customer.email || 'Booking'}
          </h1>
        </div>
        {isDesktop && (
          <div className="flex flex-shrink-0 items-center gap-3 pt-1">
            {codeText && <span className="font-mono text-sm text-stone-400">{codeText}</span>}
            {statusPill(booking)}
          </div>
        )}
      </div>

      {!isDesktop && (
        <div className="border-y border-stone-200 bg-stone-50 dark:border-stone-700 dark:bg-[#1a201d]">
          <div className="flex items-center justify-between gap-3 px-6 sm:px-8 lg:px-12 py-3">
            {codeText && (
              <span className="truncate font-mono text-sm text-stone-500 dark:text-stone-400">
                {codeText}
              </span>
            )}
            {statusPill(booking)}
          </div>
        </div>
      )}
    </>
  );
}
